import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Box, Text } from "@chakra-ui/react";

import { DocumentType, gql } from "__generated__/gql";
import FullPageTable, {
  FullPageTableProps,
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import { useAdminQuery } from "utils/apollo/hooks/admin";
import {
  OrderingPlatformFilter,
  OrderingPlatformsOrderBy,
} from "__generated__/gql/graphql";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import {
  DEFAULT_COLUMNS,
  DEFAULT_PINNED_COLUMNS,
  orderByEnumsById as baseOrderByEnumsById,
} from "components/Shared/OrderingPlatformDirectory/Table";
import { formatJustifiedDateTimeCell } from "components/Shared/ChakraReactTable/formatters";
import IsCustomBadge from "components/Shared/IsCustomBadge";
import useArchivedRowProps from "components/Shared/ChakraReactTable/useArchivedRowProps";

const query = gql(/* GraphQL */ `
  query AdminManageOrderingPlatformsTable(
    $filter: OrderingPlatformFilter
    $orderBy: [OrderingPlatformsOrderBy!] = [CREATED_AT_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    orderingPlatforms(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        id
        createdAt
        forBrandId
        forBrand {
          id
          name
        }
        ...OrderingPlatformsDirectoryTableRow
      }
    }
  }
`);

type Datum = NonNullable<
  DocumentType<typeof query>["orderingPlatforms"]
>["nodes"][number];

const col = createColumnHelper<Datum>();

const columns = [
  ...(DEFAULT_COLUMNS as ColumnDef<Datum, unknown>[]),
  col.accessor((r) => r.forBrand?.name, {
    id: "customBrand",
    header: "Custom?",
    cell: (cell) => (
      <>
        <IsCustomBadge isCustom={!!cell.row.original.forBrandId} />
        <Box fontSize="xs" color="gray.600" isTruncated>
          {cell.row.original.forBrand?.name}
        </Box>
      </>
    ),
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: (cell) => formatJustifiedDateTimeCell(cell),
    meta: { props: { textAlign: "right" } },
  }),
];

export const orderByEnumsById: GQLOrderByEnumsById<OrderingPlatformsOrderBy> = {
  ...baseOrderByEnumsById,
  customBrand: [
    OrderingPlatformsOrderBy.FOR_BRAND__NAME_ASC,
    OrderingPlatformsOrderBy.FOR_BRAND__NAME_DESC,
  ],
  createdAt: [
    OrderingPlatformsOrderBy.CREATED_AT_ASC,
    OrderingPlatformsOrderBy.CREATED_AT_DESC,
  ],
};

export type AdminManageOrderingPlatformsTableProps = Pick<
  FullPageTableProps<Datum>,
  "scrollContainerProps" | "tableContainerProps" | "tableControlsFooterProps"
> & {
  defaultPageSize?: number;
  filter?: OrderingPlatformFilter;
  isReady?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedOrderingPlatformIdChange: (newOrderingPlatformId: UUID) => void;
  selectedOrderingPlatformId: UUID | null | undefined;
};

export default function AdminManageOrderingPlatformsTable({
  defaultPageSize = 25,
  filter,
  isReady = true,
  nextFetchPolicy,
  onSelectedOrderingPlatformIdChange,
  scrollContainerProps,
  selectedOrderingPlatformId,
  tableContainerProps,
  tableControlsFooterProps,
}: AdminManageOrderingPlatformsTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: defaultPageSize });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useAdminQuery(query, {
    nextFetchPolicy,
    skip: !isReady || isPendingFilterReset || isPendingSortingReset,
    variables: { filter, orderBy, ...limitOffset },
  });

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(rawData?.orderingPlatforms?.nodes),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning: DEFAULT_PINNED_COLUMNS, pagination, sorting },
    pageCount: getPageCount(rawData?.orderingPlatforms?.totalCount),
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    useSelectableRow({
      selected: selectedOrderingPlatformId,
      onSelectedChange: onSelectedOrderingPlatformIdChange,
    }),
    useArchivedRowProps(),
    { tableProps: { variant: "condensed" } },
  ]);

  return (
    <FullPageTable
      tableInstance={tableInstance}
      {...tableProps}
      {...{
        scrollContainerProps,
        tableContainerProps,
        tableControlsFooterProps,
      }}
      isLoading={isLoading || !isReady || typeof window === "undefined"}
      noRowsPlaceholder={<Text>No ordering platforms found.</Text>}
      totalCount={rawData?.orderingPlatforms?.totalCount}
      resultTypeLabel="ordering platforms"
      onRefresh={refetch}
    />
  );
}
